import type { CharacterTalentRow, CharacterTalentTree, CharacterView } from '@thottbot/shared';
import { ArtifactLink, TooltipAnchor } from '../../components/ArtifactLink';
import { TalentsforeverCredit } from '../../components/TalentsforeverCredit';

/** The talent window's grid: four columns, one row per tier. */
const COLUMNS = 4;

function state(t: CharacterTalentRow): 'maxed' | 'learned' | 'unlearned' {
  if (t.rank <= 0) return 'unlearned';
  return t.rank >= t.maxRank ? 'maxed' : 'learned';
}

/**
 * The arrow from a prerequisite to the talent needing it, in grid units. Same column draws
 * straight down, same tier straight across, anything else an elbow (across, then down).
 */
function Connector({ from, to }: { from: CharacterTalentRow; to: CharacterTalentRow }) {
  if (!from.tier || !from.column || !to.tier || !to.column) return null;
  const down = to.tier - from.tier;
  const across = to.column - from.column;
  const parts: { kind: 'down' | 'across'; tier: number; column: number; length: number }[] = [];
  if (across !== 0) parts.push({ kind: 'across', tier: from.tier, column: Math.min(from.column, to.column), length: Math.abs(across) });
  if (down !== 0) parts.push({ kind: 'down', tier: from.tier, column: to.column, length: down });
  return (
    <>
      {parts.map((p) => (
        <span
          key={p.kind}
          className={`talent-arrow talent-arrow-${p.kind}${to.rank > 0 ? ' talent-arrow-active' : ''}`}
          style={{ ['--tier' as string]: p.tier, ['--column' as string]: p.column, ['--length' as string]: p.length }}
          aria-hidden="true"
        />
      ))}
    </>
  );
}

function Talent({ t, tree }: { t: CharacterTalentRow; tree: string }) {
  const s = state(t);
  const inner = (
    <>
      {t.iconUrl ? <img src={t.iconUrl} alt="" /> : <span className="talent-text">{t.name.slice(0, 2)}</span>}
      <span className="talent-rank">
        {t.rank}/{t.maxRank}
      </span>
    </>
  );
  const label = `${t.name}, ${t.rank} of ${t.maxRank}`;
  return (
    <li
      className={`talent talent-${s}`}
      data-testid="talent-row"
      data-tree={tree}
      data-rank={t.rank}
      style={{ gridRow: t.tier, gridColumn: t.column }}
    >
      {t.slug ? (
        <ArtifactLink type="ability" slug={t.slug} name={t.name} talent={{ rank: t.rank, maxRank: t.maxRank }} aria-label={label}>
          {inner}
        </ArtifactLink>
      ) : (
        <TooltipAnchor known={{ name: t.name }} lines={t.description ? [t.description] : []} talent={{ rank: t.rank, maxRank: t.maxRank }} aria-label={label}>
          {inner}
        </TooltipAnchor>
      )}
    </li>
  );
}

function Tree({ tree }: { tree: CharacterTalentTree }) {
  const placed = tree.talents.filter((t) => t.tier && t.column);
  const rows = Math.max(1, ...placed.map((t) => t.tier!));
  const byId = new Map(placed.filter((t) => t.artifactId).map((t) => [t.artifactId!, t]));
  return (
    <section
      className="talent-tree"
      data-testid="talent-tree"
      data-tree={tree.tree}
      data-points={tree.points}
      style={tree.backgroundUrl ? { backgroundImage: `url(${tree.backgroundUrl})` } : undefined}
    >
      <h3 className="talent-tree-title">
        {tree.iconUrl && <img className="talent-tree-icon" src={tree.iconUrl} alt="" />}
        <span>{tree.tree}</span>
        <span className="talent-tree-points" aria-label={`${tree.points} points`}>
          {tree.points}
        </span>
      </h3>
      <div className="talent-grid-wrap">
        {placed.map((t) => {
          const from = t.prerequisite && byId.get(t.prerequisite.artifactId);
          return from ? <Connector key={`${t.artifactId}-arrow`} from={from} to={t} /> : null;
        })}
        <ul
          className="talent-grid"
          aria-label={`${tree.tree} talents`}
          style={{ gridTemplateRows: `repeat(${rows}, var(--talent-cell))`, gridTemplateColumns: `repeat(${COLUMNS}, var(--talent-cell))` }}
        >
          {placed.map((t) => (
            <Talent key={t.artifactId ?? t.name} t={t} tree={tree.tree} />
          ))}
        </ul>
      </div>
    </section>
  );
}

/** The in-game talent window: the class's three trees side by side, every talent in its place. */
export function TalentTrees({ talents }: { talents: CharacterView['talents'] }) {
  const trees = talents.trees.filter((t) => t.talents.some((x) => x.tier && x.column));
  const other = talents.trees.flatMap((tree) => tree.talents.filter((t) => !t.tier || !t.column).map((t) => ({ tree: tree.tree, t })));
  if (trees.length === 0 && other.length === 0) return <p className="muted">No talent points spent.</p>;
  const spent = talents.trees.reduce((n, t) => n + t.points, 0);
  return (
    <div className="talents" data-testid="talents">
      {trees.length > 0 && (
        <div className="talent-window">
          <div className="talent-window-head">
            <span>
              Points spent <strong>{spent}</strong>
            </span>
          </div>
          <div className="talent-trees">
            {trees.map((tree) => (
              <Tree key={tree.tree} tree={tree} />
            ))}
          </div>
        </div>
      )}
      {other.length > 0 && (
        <ul className="talent-other" aria-label="Talents not placed in a tree">
          {other.map(({ tree, t }) => (
            <li key={`${tree}-${t.name}`} data-testid="talent-row" data-tree={tree} data-rank={t.rank}>
              {t.slug ? (
                <ArtifactLink type="ability" slug={t.slug} name={t.name} talent={{ rank: t.rank, maxRank: t.maxRank }} />
              ) : (
                <TooltipAnchor known={{ name: t.name }} lines={t.description ? [t.description] : []} talent={{ rank: t.rank, maxRank: t.maxRank }}>
                  {t.name}
                </TooltipAnchor>
              )}{' '}
              {t.rank}/{t.maxRank}
              {!t.slug && <span className="muted"> (not in database yet)</span>}
            </li>
          ))}
        </ul>
      )}
      {trees.length > 0 && <TalentsforeverCredit />}
    </div>
  );
}
